// src/components/LifecycleEventsList.js

import React, { useState, useEffect, useRef } from 'react';
import {
  Loader, Wrench, Recycle, AlertTriangle,
  ChevronDown, ChevronUp, Upload, CheckCircle,
  FileText, Edit2, Trash2
} from 'lucide-react';

const PRIMARY      = '#326BDF';
const API_BASE_URL = 'https://dpp-api.acespireconsulting.com/api';

const REPAIR_STATUSES  = ['In Progress', 'Completed', 'Cannot Repair'];
const RECYCLE_STATUSES = ['Received', 'Disassembling', 'Components Sorted', 'Complete'];

function formatTs(ts) {
  if (!ts) return '—';
  try {
    return new Date(ts).toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return ts; }
}

function StatusBadge({ status }) {
  const colors = {
    'In Progress':       { bg: '#FEF3C7', color: '#92400E' },
    'Completed':         { bg: '#D1FAE5', color: '#065F46' },
    'Cannot Repair':     { bg: '#FEE2E2', color: '#991B1B' },
    'Received':          { bg: '#EFF6FF', color: '#1E40AF' },
    'Disassembling':     { bg: '#FEF3C7', color: '#92400E' },
    'Components Sorted': { bg: '#F3E8FF', color: '#6B21A8' },
    'Complete':          { bg: '#D1FAE5', color: '#065F46' },
  };
  const style = colors[status] || { bg: '#F3F4F6', color: '#374151' };
  return (
    <span style={{
      display: 'inline-block',
      padding: '3px 10px', borderRadius: 20,
      fontSize: 11, fontWeight: 600,
      backgroundColor: style.bg, color: style.color,
    }}>
      {status}
    </span>
  );
}

// ── PDF Content Collapsible ───────────────────────────────────────────────────
function PdfContentCollapsible({ text }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ border: '1px solid #E2EAF8', borderRadius: 6, overflow: 'hidden' }}>
      <div
        onClick={() => setOpen(!open)}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '8px 12px', cursor: 'pointer',
          backgroundColor: '#F8FAFF',
        }}
      >
        <p style={{ fontSize: 11, fontWeight: 600, color: '#6B7280', margin: 0 }}>
          PDF CONTENT
        </p>
        {open
          ? <ChevronUp   size={13} color="#6B7280" />
          : <ChevronDown size={13} color="#6B7280" />
        }
      </div>
      {open && (
        <div style={{
          maxHeight: 200, overflowY: 'auto',
          backgroundColor: '#fff',
          padding: '10px 12px',
          borderTop: '1px solid #E2EAF8',
        }}>
          <p style={{
            fontSize: 12, color: '#374151',
            margin: 0, whiteSpace: 'pre-wrap', lineHeight: 1.6,
          }}>
            {text}
          </p>
        </div>
      )}
    </div>
  );
}

// ── Inline Edit Form ──────────────────────────────────────────────────────────
function EventEditForm({ event, userRole, onSave, onCancel }) {
  const isRepair      = event.event_type === 'Repair';
  const data          = event.event_data || {};
  const statusOptions = isRepair ? REPAIR_STATUSES : RECYCLE_STATUSES;

  const currentStatus = isRepair
    ? (data.repair_status     || REPAIR_STATUSES[0])
    : (data.disassembly_stage || RECYCLE_STATUSES[0]);

  const [status,    setStatus]    = useState(currentStatus);
  const [component, setComponent] = useState(data.component || '');
  const [notes,     setNotes]     = useState(data.notes     || '');
  const [pdfFile,   setPdfFile]   = useState(null);
  const [saving,    setSaving]    = useState(false);
  const [saveError, setSaveError] = useState(null);
  const fileInputRef              = useRef(null);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) setPdfFile(file);
  };

  const handleSubmit = async () => {
    try {
      setSaving(true);
      setSaveError(null);

      const token = localStorage.getItem('id_token') || localStorage.getItem('access_token') || localStorage.getItem('authToken');

      const formData = new FormData();
      formData.append('status', status);

      if (isRepair) {
        formData.append('component', component);
        formData.append('notes',     notes);
      }

      if (pdfFile) formData.append('file', pdfFile);

      const res = await fetch(`${API_BASE_URL}/lifecycle-events/${event.id}`, {
        method:  'PATCH',
        headers: { Authorization: `Bearer ${token}` },
        body:    formData,
      });

      const result = await res.json();

      if (result.success) {
        onSave(result.data);
      } else {
        setSaveError(result.message || 'Failed to update event');
      }
    } catch (err) {
      setSaveError(err.message || 'Failed to update event');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{
      marginTop: 12, padding: 14,
      backgroundColor: '#F8FAFF',
      borderRadius: 8, border: '1px solid #DBEAFE',
      display: 'flex', flexDirection: 'column', gap: 12,
    }}>

      {/* Status dropdown */}
      <div>
        <p style={{ fontSize: 11, fontWeight: 600, color: '#6B7280', marginBottom: 6 }}>
          {isRepair ? 'REPAIR STATUS' : 'DISASSEMBLY STAGE'}
        </p>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          style={{
            width: '100%', padding: '8px 10px',
            borderRadius: 6, border: '1px solid #BFDBFE',
            fontSize: 13, color: '#111827',
            backgroundColor: '#fff', outline: 'none',
            cursor: 'pointer',
          }}
        >
          {statusOptions.map(s => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      </div>

      {/* Component + Notes — Repair only */}
      {isRepair && (
        <>
          <div>
            <p style={{ fontSize: 11, fontWeight: 600, color: '#6B7280', marginBottom: 6 }}>
              COMPONENT
            </p>
            <input
              type="text"
              value={component}
              onChange={(e) => setComponent(e.target.value)}
              placeholder="e.g. Screen, Battery"
              style={{
                width: '100%', padding: '8px 10px',
                borderRadius: 6, border: '1px solid #BFDBFE',
                fontSize: 13, color: '#111827',
                backgroundColor: '#fff', outline: 'none',
                boxSizing: 'border-box',
              }}
            />
          </div>
          <div>
            <p style={{ fontSize: 11, fontWeight: 600, color: '#6B7280', marginBottom: 6 }}>
              TECHNICIAN NOTES
            </p>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add technician notes..."
              rows={3}
              style={{
                width: '100%', padding: '8px 10px',
                borderRadius: 6, border: '1px solid #BFDBFE',
                fontSize: 13, color: '#111827',
                backgroundColor: '#fff', outline: 'none',
                resize: 'vertical', boxSizing: 'border-box',
              }}
            />
          </div>
        </>
      )}

      {/* PDF Upload */}
      <div>
        <p style={{ fontSize: 11, fontWeight: 600, color: '#6B7280', marginBottom: 6 }}>
          {isRepair ? 'REPAIR REPORT (PDF)' : 'RECYCLING CERTIFICATE (PDF)'}
          <span style={{ fontWeight: 400, marginLeft: 4 }}>— optional</span>
        </p>
        <div
          onClick={() => fileInputRef.current?.click()}
          style={{
            border: `1.5px dashed #BFDBFE`,
            borderRadius: 8, padding: '12px 16px',
            backgroundColor: '#EFF6FF',
            cursor: 'pointer', textAlign: 'center',
            transition: 'all 0.15s',
          }}
        >
          {pdfFile ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
              <FileText size={16} color={PRIMARY} />
              <span style={{ fontSize: 13, color: PRIMARY, fontWeight: 600 }}>{pdfFile.name}</span>
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
              <Upload size={16} color={PRIMARY} />
              <span style={{ fontSize: 13, color: PRIMARY }}>Click to upload PDF</span>
            </div>
          )}
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="application/pdf"
          onChange={handleFileChange}
          style={{ display: 'none' }}
        />
        {!pdfFile && data.pdf_filename && (
          <p style={{ fontSize: 11, color: '#6B7280', marginTop: 6 }}>
            Current: <span style={{ color: '#374151', fontWeight: 600 }}>{data.pdf_filename}</span>
            {' '}· uploaded {formatTs(data.pdf_uploaded_at)}
          </p>
        )}
      </div>

      {/* Error */}
      {saveError && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#B91C1C' }}>
          <AlertTriangle size={13} />
          <span style={{ fontSize: 12 }}>{saveError}</span>
        </div>
      )}

      {/* Actions */}
      <div style={{ display: 'flex', gap: 8 }}>
        <button
          onClick={handleSubmit}
          disabled={saving}
          style={{
            flex: 1, padding: '8px 0',
            backgroundColor: saving ? '#93C5FD' : PRIMARY,
            color: '#fff', border: 'none',
            borderRadius: 6, fontSize: 13,
            fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
          }}
        >
          {saving
            ? <><Loader size={13} className="animate-spin" /> Saving...</>
            : <><CheckCircle size={13} /> Save Changes</>
          }
        </button>
        <button
          onClick={onCancel}
          disabled={saving}
          style={{
            padding: '8px 16px',
            backgroundColor: '#F3F4F6', color: '#374151',
            border: '1px solid #D1D5DB', borderRadius: 6,
            fontSize: 13, fontWeight: 600,
            cursor: saving ? 'not-allowed' : 'pointer',
          }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

// ── Event Card ────────────────────────────────────────────────────────────────
function EventCard({ event: initialEvent, userRole, canEdit, onDelete }) {
  const [event,       setEvent]       = useState(initialEvent);
  const [expanded,    setExpanded]    = useState(false);
  const [editing,     setEditing]     = useState(false);
  const [deletingPdf, setDeletingPdf] = useState(false);
  const [deletingEvt, setDeletingEvt] = useState(false);

  const isRepair  = event.event_type === 'Repair';
  const isRecycle = event.event_type === 'Recycle';
  const data      = event.event_data || {};

  const handleSave = (updatedEvent) => {
    setEvent(updatedEvent);
    setEditing(false);
  };

  const handleDeletePdf = async () => {
    if (!window.confirm('Are you sure you want to delete this PDF?')) return;
    try {
      setDeletingPdf(true);
      const token = localStorage.getItem('id_token') || localStorage.getItem('access_token') || localStorage.getItem('authToken');
      const res = await fetch(`${API_BASE_URL}/lifecycle-events/${event.id}/pdf`, {
        method:  'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      const result = await res.json();
      if (result.success) {
        setEvent(prev => ({ ...prev, event_data: result.data.event_data }));
      } else {
        alert(result.message || 'Failed to delete PDF');
      }
    } catch (err) {
      alert(err.message || 'Failed to delete PDF');
    } finally {
      setDeletingPdf(false);
    }
  };

  const handleDeleteEvent = async () => {
    if (!window.confirm(`Are you sure you want to delete this ${isRepair ? 'repair' : 'recycling'} event? This cannot be undone.`)) return;
    try {
      setDeletingEvt(true);
      const token = localStorage.getItem('id_token') || localStorage.getItem('access_token') || localStorage.getItem('authToken');
      const res = await fetch(`${API_BASE_URL}/lifecycle-events/${event.id}`, {
        method:  'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      const result = await res.json();
      if (result.success) {
        onDelete(event.id);
      } else {
        alert(result.message || 'Failed to delete event');
      }
    } catch (err) {
      alert(err.message || 'Failed to delete event');
    } finally {
      setDeletingEvt(false);
    }
  };

  return (
    <div style={{
      border: '1px solid #BFDBFE',
      borderRadius: 10, overflow: 'hidden',
      backgroundColor: '#fff',
    }}>

      {/* Header */}
      <div
        onClick={() => { setExpanded(!expanded); if (editing) setEditing(false); }}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '12px 16px', cursor: 'pointer',
          backgroundColor: '#F8FAFF',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            width: 32, height: 32, borderRadius: 8,
            backgroundColor: '#BFDBFE',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            {isRepair
              ? <Wrench  size={15} color="#1D4ED8" />
              : <Recycle size={15} color="#1D4ED8" />
            }
          </div>
          <div>
            <p style={{ fontSize: 13, fontWeight: 700, color: '#111827', margin: 0 }}>
              {isRepair ? 'Repair Event' : 'Recycling Event'}
            </p>
            <p style={{ fontSize: 11, color: '#6B7280', margin: 0 }}>
              {formatTs(event.created_at)} · {event.submitted_by}
            </p>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {isRepair  && data.repair_status     && <StatusBadge status={data.repair_status} />}
          {isRecycle && data.disassembly_stage && <StatusBadge status={data.disassembly_stage} />}
          {expanded
            ? <ChevronUp   size={16} color="#6B7280" />
            : <ChevronDown size={16} color="#6B7280" />
          }
        </div>
      </div>

      {/* Details */}
      {expanded && (
        <div style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>

          {/* Repair fields */}
          {isRepair && (
            <>
              {data.component && (
                <div>
                  <p style={{ fontSize: 11, fontWeight: 600, color: '#6B7280', marginBottom: 2 }}>COMPONENT</p>
                  <p style={{ fontSize: 13, color: '#111827', margin: 0 }}>{data.component}</p>
                </div>
              )}
              {data.notes && (
                <div>
                  <p style={{ fontSize: 11, fontWeight: 600, color: '#6B7280', marginBottom: 2 }}>TECHNICIAN NOTES</p>
                  <p style={{ fontSize: 13, color: '#374151', margin: 0 }}>{data.notes}</p>
                </div>
              )}
            </>
          )}

          {/* Recycle fields */}
          {isRecycle && (
            <>
              {data.disposal_method && (
                <div>
                  <p style={{ fontSize: 11, fontWeight: 600, color: '#6B7280', marginBottom: 2 }}>DISPOSAL METHOD</p>
                  <p style={{ fontSize: 13, color: '#111827', margin: 0 }}>{data.disposal_method}</p>
                </div>
              )}
              {data.materials_recovered && (
                <div>
                  <p style={{ fontSize: 11, fontWeight: 600, color: '#6B7280', marginBottom: 2 }}>MATERIALS RECOVERED</p>
                  <p style={{ fontSize: 13, color: '#374151', margin: 0 }}>{data.materials_recovered}</p>
                </div>
              )}
            </>
          )}

          {/* Location */}
          {event.location_id && (
            <div>
              <p style={{ fontSize: 11, fontWeight: 600, color: '#6B7280', marginBottom: 2 }}>LOCATION</p>
              <p style={{ fontSize: 13, color: '#111827', margin: 0 }}>{event.location_id}</p>
            </div>
          )}

          {/* PDF badge + collapsible content */}
          {data.pdf_filename && !editing && (
            <div style={{
              display: 'flex', flexDirection: 'column', gap: 8,
              padding: '10px 12px',
              backgroundColor: '#F8FAFF',
              borderRadius: 8, border: '1px solid #E2EAF8',
            }}>
              {/* Filename row */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <FileText size={13} color={PRIMARY} />
                  <span style={{ fontSize: 12, color: '#374151', fontWeight: 600 }}>
                    {data.pdf_filename}
                  </span>
                  <span style={{ fontSize: 11, color: '#6B7280', marginLeft: 4 }}>
                    · {formatTs(data.pdf_uploaded_at)}
                  </span>
                </div>
                {canEdit && (
                  <button
                    onClick={(e) => { e.stopPropagation(); handleDeletePdf(); }}
                    disabled={deletingPdf}
                    style={{
                      padding: '4px 8px',
                      backgroundColor: '#FEE2E2', color: '#991B1B',
                      border: '1px solid #FECACA', borderRadius: 6,
                      fontSize: 11, fontWeight: 600,
                      cursor: deletingPdf ? 'not-allowed' : 'pointer',
                      display: 'flex', alignItems: 'center', gap: 4,
                    }}
                  >
                    {deletingPdf
                      ? <Loader size={11} className="animate-spin" />
                      : <Trash2 size={11} />
                    }
                    Delete PDF
                  </button>
                )}
              </div>

              {/* Collapsible PDF text */}
              {data.pdf_text && <PdfContentCollapsible text={data.pdf_text} />}
            </div>
          )}

          {/* Action buttons — only for canEdit roles */}
          {canEdit && !editing && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 2 }}>
              <button
                onClick={(e) => { e.stopPropagation(); setEditing(true); }}
                style={{
                  padding: '6px 12px',
                  backgroundColor: '#EFF6FF',
                  border: '1px solid #BFDBFE',
                  borderRadius: 6, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: 6,
                }}
                title="Update event"
              >
                <Edit2 size={13} color="#326BDF" />
                <span style={{ fontSize: 12, fontWeight: 600, color: '#326BDF' }}>Update Event</span>
              </button>

              <button
                onClick={(e) => { e.stopPropagation(); handleDeleteEvent(); }}
                disabled={deletingEvt}
                style={{
                  padding: '6px 12px',
                  backgroundColor: '#FEF2F2',
                  border: '1px solid #FECACA',
                  borderRadius: 6, cursor: deletingEvt ? 'not-allowed' : 'pointer',
                  display: 'flex', alignItems: 'center', gap: 6,
                }}
                title="Delete event"
              >
                {deletingEvt
                  ? <Loader size={13} className="animate-spin" color="#991B1B" />
                  : <Trash2 size={13} color="#991B1B" />
                }
                <span style={{ fontSize: 12, fontWeight: 600, color: '#991B1B' }}>Delete Event</span>
              </button>
            </div>
          )}

          {/* Inline edit form */}
          {editing && (
            <EventEditForm
              event={event}
              userRole={userRole}
              onSave={handleSave}
              onCancel={() => setEditing(false)}
            />
          )}
        </div>
      )}
    </div>
  );
}

// ── LifecycleEventsList ───────────────────────────────────────────────────────
function LifecycleEventsList({ dppId, userRole }) {
  const [events,    setEvents]    = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState(null);
  const [collapsed, setCollapsed] = useState(false);

  // Consumer added — sees all events read-only
  const isVisible = ['Repair/Refurb', 'Recycler', 'Manufacturer', 'Consumer'].includes(userRole);

  const canEditEvent = (event) => {
    if (userRole === 'Repair/Refurb' && event.event_type === 'Repair')  return true;
    if (userRole === 'Recycler'       && event.event_type === 'Recycle') return true;
    return false;
  };

  const handleEventDeleted = (deletedId) => {
    setEvents(prev => prev.filter(e => e.id !== deletedId));
  };

  useEffect(() => {
    if (!dppId || !isVisible) { setLoading(false); return; }
    const fetchEvents = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('id_token') || localStorage.getItem('access_token') || localStorage.getItem('authToken');
        const res = await fetch(`${API_BASE_URL}/lifecycle-events/${dppId}`, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
        });
        const data = await res.json();
        if (data.success) {
          setEvents(data.data || []);
        } else {
          setError(data.message || 'Failed to load events');
        }
      } catch (err) {
        setError(err.message || 'Failed to load events');
      } finally {
        setLoading(false);
      }
    };
    fetchEvents();
  }, [dppId]);

  if (!isVisible) return null;

  const title = userRole === 'Manufacturer' || userRole === 'Consumer'
    ? 'Lifecycle Events'
    : userRole === 'Repair/Refurb'
      ? 'Repair Events'
      : 'Recycling Events';

  const emptyMessage = userRole === 'Repair/Refurb'
    ? 'repair'
    : userRole === 'Recycler'
      ? 'recycling'
      : 'lifecycle';

  return (
    <div style={{
      border: '1px solid #E2EAF8', borderRadius: 12,
      overflow: 'hidden',
      boxShadow: '0 1px 4px rgba(50,107,223,0.06)',
    }}>

      {/* Collapsible Header */}
      <div
        onClick={() => setCollapsed(!collapsed)}
        style={{
          padding: '14px 20px',
          borderBottom: collapsed ? 'none' : '1px solid #E2EAF8',
          backgroundColor: '#F8FAFF',
          cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}
      >
        <div>
          <h3 style={{ fontSize: 14, fontWeight: 700, color: '#111827', margin: '0 0 2px' }}>
            {title}
          </h3>
          <p style={{ fontSize: 11, color: '#6B7280', margin: 0 }}>
            {loading ? 'Loading...' : `${events.length} event${events.length !== 1 ? 's' : ''} recorded`}
          </p>
        </div>
        {collapsed
          ? <ChevronDown size={16} color="#6B7280" />
          : <ChevronUp   size={16} color="#6B7280" />
        }
      </div>

      {/* Content */}
      {!collapsed && (
        <div style={{ padding: 16, backgroundColor: '#fff' }}>
          {loading ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#9CA3AF', padding: '12px 0' }}>
              <Loader size={16} className="animate-spin" />
              <span style={{ fontSize: 13 }}>Loading events...</span>
            </div>
          ) : error ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#B91C1C', padding: '8px 0' }}>
              <AlertTriangle size={14} />
              <span style={{ fontSize: 13 }}>{error}</span>
            </div>
          ) : events.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '24px 0', color: '#9CA3AF' }}>
              <p style={{ fontSize: 13, margin: 0 }}>
                No {emptyMessage} events recorded yet.
              </p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {events.map(event => (
                <EventCard
                  key={event.id}
                  event={event}
                  userRole={userRole}
                  canEdit={canEditEvent(event)}
                  onDelete={handleEventDeleted}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default LifecycleEventsList;